import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/Music";
import { api, apiError, formatDate } from "@/lib/api";
import { cn } from "@/lib/utils";

/** Oldest-added copy first — the one we suggest keeping. */
function suggestKeep(tracks) {
  return [...tracks].sort((a, b) => (a.release_date || "9999").localeCompare(b.release_date || "9999") || (a.created_at || "").localeCompare(b.created_at || ""))[0]?.track_id;
}

function Step({ n, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 font-mono text-[10.5px] font-semibold tracking-[0.12em] uppercase text-muted2">
        <span className="inline-grid place-items-center w-[18px] h-[18px] rounded-full bg-brand text-white text-[10px] tracking-normal">{n}</span>
        {title}
      </div>
      {children}
    </div>
  );
}

/**
 * Merge copies of the same recording (same ISRC) into one track that sits on every album the copies were on.
 * The server works out the plan (POST /tracks/merge/preview) so what you see is exactly what happens.
 */
export default function MergeDialog({ tracks, open, onOpenChange, onMerged }) {
  const [keep, setKeep] = useState(null);
  const [plan, setPlan] = useState(null);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);
  const ids = (tracks || []).map((t) => t.track_id);
  const idKey = ids.join(",");

  useEffect(() => {
    if (open) {
      setKeep(suggestKeep(tracks || []));
      setPicks({});
      setPlan(null);
    }
  }, [open, idKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !keep) return undefined;
    let live = true;
    api
      .post("/tracks/merge/preview", { track_ids: ids, keep_id: keep })
      .then(({ data }) => {
        if (!live) return;
        setPlan(data);
        setPicks((p) => Object.fromEntries(data.diffs.map((d) => [d.field, p[d.field] && d.options.some((o) => o.track_id === p[d.field]) ? p[d.field] : d.default])));
      })
      .catch((err) => {
        if (live) {
          toast.error(apiError(err));
          onOpenChange(false);
        }
      });
    return () => {
      live = false;
    };
  }, [open, keep, idKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyName = (c) => (c.riddim ? `On ${c.riddim}` : c.release_date ? `Released ${formatDate(c.release_date)}` : "No album");
  const kept = plan?.copies.find((c) => c.track_id === keep);
  // a new album whose copies disagreed on its date shows the date you picked
  const albumDate = (r) => {
    const d = plan?.diffs.find((x) => x.field === `album_date:${r.key}`);
    return d ? d.options.find((o) => o.track_id === picks[d.field])?.value || r.date : r.date;
  };
  const albumDates = (plan?.releases || []).filter((r) => r.kind === "album").map(albumDate).filter(Boolean).sort();
  const ownDate = plan && (plan.result.album_only ? albumDates[0] : plan.result.release_date);
  const removing = plan?.copies.filter((c) => c.track_id !== keep) || [];

  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tracks/merge", { track_ids: ids, keep_id: keep, picks });
      toast.success(`Merged — ${kept?.title || "the track"} is now one track on ${data.albums} album${data.albums === 1 ? "" : "s"}`);
      onOpenChange(false);
      onMerged?.(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-[780px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            Merge {ids.length} copies of {tracks?.[0]?.title || "this track"}
          </DialogTitle>
          <div className="text-[13px] text-muted2">
            ISRC <span className="font-mono">{tracks?.[0]?.isrc}</span> · {tracks?.[0]?.artist}
          </div>
        </DialogHeader>
        {!plan ? (
          <div className="py-12 grid place-items-center text-muted2">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <div className="overflow-y-auto -mx-6 px-6 space-y-5 pb-1">
            {plan.diffs.some((d) => d.field === "title") && (
              <div className="rounded-xl border border-stock-out/40 bg-rose-50 dark:bg-rose-950/30 px-3.5 py-2.5 text-[13px]" role="alert">
                <b>These have different titles</b> ({plan.copies.map((c) => c.title).join(" / ")}). If they're different songs, one of them has the wrong ISRC — fix that
                on its track page instead of merging. Only merge if they're the same recording.
              </div>
            )}
            <Step n={1} title="Track to keep">
              <div className="grid gap-2.5 sm:grid-cols-2">
                {plan.copies.map((c) => (
                  <label key={c.track_id} className={cn("flex gap-2.5 items-start rounded-xl border p-3 cursor-pointer", keep === c.track_id ? "border-brand bg-brand-light" : "border-line")}>
                    <input type="radio" name="keep" className="mt-1 accent-brand" checked={keep === c.track_id} onChange={() => setKeep(c.track_id)} />
                    <span className="min-w-0 text-[12.5px]">
                      <b className="text-[13px]">{copyName(c)}</b> {c.track_id === suggestKeep(plan.copies) && <Pill tone="muted">Oldest</Pill>}
                      <span className="grid grid-cols-[auto_1fr] gap-x-2.5 mt-1.5">
                        <span className="text-muted2">Released</span>
                        <span>{c.release_date ? formatDate(c.release_date) : "—"}</span>
                        <span className="text-muted2">UPC</span>
                        <span className="font-mono text-[11.5px]">{c.upc || "—"}</span>
                        <span className="text-muted2">Distributor</span>
                        <span>{c.distributor || "—"}</span>
                        <span className="text-muted2">Royalty lines</span>
                        <span>{c.royalty_lines}</span>
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-[12px] text-muted2 mt-1.5">The other cop{removing.length === 1 ? "y is" : "ies are"} removed. Links to {removing.length === 1 ? "it" : "them"} open the track you keep.</p>
            </Step>

            <Step n={2} title={`Where it will be released`}>
              <div className="rounded-xl border border-line overflow-x-auto">
                <table className="w-full min-w-[520px]">
                  <thead>
                    <tr className="border-b border-line">
                      {["", "Release", "Date", "UPC", "Distributor", ""].map((h, i) => (
                        <th key={i} className="t-head text-left px-3 py-2 font-medium">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {plan.releases.map((r, i) => (
                      <tr key={i} className="border-t border-line first:border-0 text-[12.5px]">
                        <td className="px-3 py-2">
                          <Pill tone={r.kind === "album" ? "brand" : "muted"}>{r.kind === "album" ? "Album" : "Single"}</Pill>
                        </td>
                        <td className="px-3 py-2 font-semibold">{r.name}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{albumDate(r) ? formatDate(albumDate(r)) : "—"}</td>
                        <td className="px-3 py-2 font-mono text-[11.5px]">{r.upc || "—"}</td>
                        <td className="px-3 py-2">{r.distributor || "—"}</td>
                        <td className="px-3 py-2">{r.kind !== "album" ? null : r.exists ? <Pill tone="good">Already an album</Pill> : <Pill tone="warn">Will be created</Pill>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[12px] text-muted2 mt-1.5">
                Each copy's own release moves onto its album. The track's own date becomes {ownDate ? formatDate(ownDate) : "blank"}
                {plan.result.album_only ? (
                  <>
                    , so it shows as <b>Album only</b> — no Single row.
                  </>
                ) : (
                  ", its single."
                )}
              </p>
            </Step>

            <Step n={3} title="Where the copies differ">
              {plan.diffs.length === 0 ? (
                <p className="text-[13px]">Nothing — title, artist, writers &amp; splits and everything else match.</p>
              ) : (
                <div className="space-y-2">
                  {plan.diffs.map((d) => (
                    <div key={d.field} className="flex gap-2.5 items-start flex-wrap">
                      <span className="w-[170px] shrink-0 text-[13px] font-semibold pt-1">{d.label}</span>
                      <div className="flex gap-1.5 flex-wrap min-w-0">
                        {d.options.map((o) => (
                          <button
                            key={o.track_id}
                            type="button"
                            aria-pressed={picks[d.field] === o.track_id}
                            onClick={() => setPicks((p) => ({ ...p, [d.field]: o.track_id }))}
                            className={cn("rounded-lg border px-2.5 py-1 text-[12.5px] text-left max-w-full", picks[d.field] === o.track_id ? "border-brand bg-brand-light font-semibold" : "border-line hover:bg-surface")}
                          >
                            {d.field.startsWith("album_date:") ? `${formatDate(o.value)} · from the ${plan.copies.find((c) => c.track_id === o.track_id)?.riddim || "other"} copy` : o.value}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-[12px] text-muted2">Only fields that differ are shown. Anything only one copy filled in is kept.</p>
                </div>
              )}
            </Step>

            <Step n={4} title="Carried over to the kept track">
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[12.5px]">
                {[
                  `Royalty lines from both (${plan.moving_lines} moving over)`,
                  "Royalty links and disputes",
                  "Registrations (ticked if either copy was)",
                  "History from every copy",
                  "Album memberships",
                  "Payouts already made — unchanged",
                ].map((x) => (
                  <div key={x}>
                    <span className="text-stock-in font-bold">✓</span> {x}
                  </div>
                ))}
              </div>
            </Step>
          </div>
        )}
        <DialogFooter className="items-center gap-2 sm:gap-2">
          {kept && (
            <span className="text-[12px] text-muted2 mr-auto">
              Keeping <b>{copyName(kept)}</b> · removing {removing.map((c) => copyName(c)).join(", ")}
            </span>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button className="bg-brand hover:bg-brand-hover" onClick={merge} disabled={!plan || busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Merge into one track"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
